import React, { useEffect, useState } from 'react';
import './styles/EndModal.css';
import { getGameDataProperty } from '../../services/state';
import { convertMillisToSeconds, shareOrCopyText } from '../../helper/helperFunctions';
import BoardButton from '../BoardButton';

// Map action to emoji for sharing
const mapActionToEmoji = (act) => {
    if (act === 'U') return '🟨';
    if (act === 'C') return '⬛';
    if (typeof act === 'string' && /^[+\-*/]/.test(act)) return '🟩';
    return '⬜';
};

// Chunk emojis into lines of 5
const chunkEmojis = (emojiStr) => Array.from(emojiStr).reduce((acc, char, idx) =>
    idx % 5 === 0 ? [...acc, char] : [...acc.slice(0, -1), acc[acc.length - 1] + char], []
).join('\n');

// Exported helper so tests can call it directly
export const buildShareText = (board) => {
    const score = getGameDataProperty(board, 'gameScore') || 0;
    const actionHistory = getGameDataProperty(board, 'action_history') || [];
    const emojiRow = actionHistory.map(mapActionToEmoji).join('') || '⬜';
    const body = chunkEmojis(emojiRow);
    return `Da Combos #${board}\nScore: ${score}\n\n${body}`;
};

// New: map action to display text and color for modal
const mapActionToLabelAndColor = (act) => {
    if (act === 'U') return { text: 'undo', color: '#f1c40f' }; // yellow
    if (act === 'C') return { text: 'clear', color: '#929d9e' }; // gray
    if (typeof act === 'string' && /^[+\-*/]\d+/.test(act)) {
        return { text: act, color: '#00d93a' }; // green for operators like +7 or -3
    }
    // fallback
    return { text: String(act), color: '#000' };
};

// New: produce JSX of comma-separated colored operation text in CP font
const getActionHistoryElements = (boardNum) => {
    const history = getGameDataProperty(boardNum, 'action_history') || [];
    if (!history.length) return null;
    const labeled = history.map(mapActionToLabelAndColor);
    return (
        <div className='end-modal-history' style={{ fontFamily: 'CP, monospace', whiteSpace: 'pre-wrap', letterSpacing: '0.05em' }}>
            {labeled.map((item, idx) => (
                <span key={idx} style={{ color: item.color, fontWeight: 600 }}>
                    {item.text}{idx < labeled.length - 1 ? ', ' : ''}
                </span>
            ))}
        </div>
    );
};

const EndModal = ({ setShowEndModal, boardNum }) => {
    const [feedback, setFeedback] = useState('');

    useEffect(() => {
        const handleClickOutside = (e) => e.target?.classList?.contains('end-modal') && setShowEndModal(false);
        const handleEscape = (e) => (e.key === 'Escape' || e.key === 'Esc') && setShowEndModal(false);

        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [setShowEndModal]);

    const handleShareClick = async () => {
        try {
            const result = await shareOrCopyText(buildShareText(boardNum));
            setFeedback(result === 'shared' ? 'Shared' : 'Link copied to clipboard');
        } catch {
            setFeedback('Failed to share');
        }
        setTimeout(() => setFeedback(''), 3000);
    };

    return (
        <div className='end-modal' role='dialog' aria-modal='true' tabIndex='-1'>
            <div className='end-modal-content'>
                <button className='end-modal-close' aria-label='Close' onClick={() => setShowEndModal(false)}>×</button>
                <h2 className='end-modal-title'>Well Done!</h2>
                <p className="end-modal-meta">Time: {convertMillisToSeconds(getGameDataProperty(boardNum, 'timeTaken'))}s</p>
                <p className="end-modal-meta">Score: <strong>{getGameDataProperty(boardNum, 'gameScore')}</strong></p>
                <p className="end-modal-meta" style={{ marginTop: '1rem', fontSize: '1.1rem', marginBottom: 0 }}>Actions Taken:</p>

                {getActionHistoryElements(boardNum)}
                <div style={{ margin: '1rem', display: 'flex', gap: '0.5rem' }}>
                    <BoardButton className='function-button share-button' onClick={handleShareClick} label={'Share'}>Share</BoardButton>
                </div>
                {feedback && <p style={{ fontSize: '1rem', margin: 0, lineHeight: 0 }}>{feedback}</p>}
            </div>
        </div >
    );
};

export default EndModal;